//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { View, ViewOff } from '@carbon/icons-react';
import { ChevronDown } from 'lucide-react';
import { ProviderIcon } from './providerIcons';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from '../ui/select';
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from '../ui/collapsible';
import { useConfig } from '../../Config';
import type AiDataService from '../../utils/AiDataService';
import {
  REASONING_MODEL_PROVIDERS,
  getAvailableProviders,
  defaultValuesForProvider,
  integrationSchema,
  type IntegrationFormValues,
  type Provider,
} from './integrationSchema';

interface IntegrationFormProps {
  mode: 'create' | 'edit' | 'view';
  initialData?: Partial<IntegrationFormValues> & { id?: string };
  isNameUnique: (name: string, originalName?: string) => boolean;
  aiDataService: AiDataService;
  teamspaceId?: string;
  onSubmit: (values: IntegrationFormValues) => void | Promise<void>;
  onCancel: () => void;
  isSaving?: boolean;
}

const inputClasses =
  'w-full bg-white dark:bg-[#262626] border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 placeholder-gray-400';

const IntegrationForm: React.FC<IntegrationFormProps> = ({
  mode,
  initialData,
  isNameUnique,
  aiDataService,
  teamspaceId,
  onSubmit,
  onCancel,
  isSaving = false,
}) => {
  const { t } = useTranslation();
  const { services } = useConfig();
  const readOnly = mode === 'view';

  const initialProvider = (initialData?.provider as Provider) || 'openai';

  // initialData may own keys with an `undefined` value (e.g. a legacy
  // integration record that predates a field this form now defaults, such
  // as reasoningEnabled) — plain object spread only preserves the schema
  // default for keys initialData omits entirely, not ones it owns with an
  // undefined value. Stripping those keys first means a real value (even
  // `false`) always wins, but an explicit `undefined` never clobbers the
  // schema default.
  const definedInitialData = initialData
    ? (Object.fromEntries(
        Object.entries(initialData).filter(([, v]) => v !== undefined),
      ) as Partial<IntegrationFormValues>)
    : {};

  // NEXUS00007282: defaultValuesForProvider now defaults a fresh
  // openai/claude/gemini integration to reasoningEnabled: true. That default
  // must apply only to a genuine new integration (no initialData at all) —
  // not to editing/viewing an existing record whose stored reasoningEnabled
  // is undefined (a legacy row predating the field). Without this override,
  // reopening any such legacy row would resubmit reasoningEnabled: true on
  // save, silently changing behavior for an already-configured integration
  // the moment someone touches it — exactly what this change must not do.
  const providerDefaults = defaultValuesForProvider(
    initialProvider,
    initialData?.name ?? '',
  );
  const baseDefaults = initialData
    ? { ...providerDefaults, reasoningEnabled: false }
    : providerDefaults;

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<IntegrationFormValues>({
    resolver: zodResolver(integrationSchema),
    defaultValues: {
      ...baseDefaults,
      ...definedInitialData,
    } as IntegrationFormValues,
    mode: 'onChange',
  });

  const provider = watch('provider');
  const name = watch('name');
  const apiKey = (watch as any)('apiKey') as string | undefined;
  const model = (watch as any)('model') as string | undefined;
  const ollamaReasoningEnabled = !!(watch as any)('reasoningEnabled');
  const apiEndpoint = (watch as any)('apiEndpoint') as string | undefined;

  const [showApiKey, setShowApiKey] = useState(false);
  const [nameWarning, setNameWarning] = useState('');
  const [modelOptions, setModelOptions] = useState<string[]>([]);
  const [modelsError, setModelsError] = useState<string | null>(null);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [reasoningModels, setReasoningModels] = useState<Set<string>>(
    new Set(),
  );

  // Switching provider in create mode resets to that provider's empty shape,
  // keeping only the name the user already typed.
  const handleProviderChange = (next: Provider) => {
    if (mode !== 'create') return;
    reset(defaultValuesForProvider(next, name));
    setModelOptions([]);
    setModelsError(null);
  };

  useEffect(() => {
    const trimmed = (name || '').trim();
    if (!trimmed) {
      setNameWarning('');
      return;
    }
    if (!/^[a-zA-Z0-9 _-]*$/.test(trimmed)) {
      setNameWarning(t('specialCharactersNotAllowed'));
      return;
    }
    const unique = isNameUnique(
      name,
      mode === 'create' ? undefined : initialData?.name,
    );
    setNameWarning(unique ? '' : t('name-must-be-unique'));
  }, [name, isNameUnique, initialData?.name, mode, t]);

  // Model discovery: 400ms debounce, keyed on provider + apiKey + apiEndpoint
  // (aiDataService deliberately excluded — see Global Constraints), no
  // fallback to the catalogue on a live-fetch failure.
  useEffect(() => {
    if (provider === 'azure-openai' || provider === 'azure-openai-gateway') {
      setModelOptions([]);
      setModelsError(null);
      return;
    }

    let cancelled = false;
    const key = (apiKey || '').trim();
    const endpoint = (apiEndpoint || '').trim() || undefined;

    const applyModels = (data: {
      models?: string[];
      reasoning_models?: string[];
    }) => {
      if (cancelled) return;
      setModelsError(null);
      setModelOptions(Array.isArray(data.models) ? data.models : []);
      setReasoningModels(new Set(data.reasoning_models ?? []));
    };

    const blockWithError = (message: string) => {
      if (cancelled) return;
      setModelOptions([]);
      setReasoningModels(new Set());
      setModelsError(message);
    };

    const fetchCatalogue = () => {
      const baseUrl = (services?.analyticsai?.url || '').replace(/\/$/, '');
      const url = `${baseUrl}/models?provider=${encodeURIComponent(provider)}`;
      setModelsLoading(true);
      fetch(url)
        .then((res) => (res.ok ? res.json() : { models: [] }))
        .then(applyModels)
        .catch(() => {
          if (!cancelled) setModelOptions([]);
        })
        .finally(() => {
          if (!cancelled) setModelsLoading(false);
        });
    };

    const handle = setTimeout(() => {
      if (key && typeof aiDataService?.getModelsForKey === 'function') {
        setModelsLoading(true);
        aiDataService
          .getModelsForKey(
            provider,
            key,
            endpoint,
            teamspaceId,
            initialData?.id,
          )
          .then((data) => {
            if (data?.ok === false) {
              blockWithError(
                data?.error?.message ||
                  'Unable to fetch models. Please verify the API key and endpoint.',
              );
            } else {
              applyModels(data);
            }
          })
          .catch(() =>
            blockWithError(
              'Unable to fetch models. Please verify the API key and endpoint.',
            ),
          )
          .finally(() => {
            if (!cancelled) setModelsLoading(false);
          });
      } else {
        setModelsError(null);
        fetchCatalogue();
      }
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider, services?.analyticsai?.url, apiKey, apiEndpoint]);

  const showsReasoning =
    REASONING_MODEL_PROVIDERS.has(provider) &&
    !!model &&
    reasoningModels.has(model);

  // Save stays clickable at all times; clicking it while required fields are
  // blank runs the normal schema validation instead of blocking the click,
  // which highlights every unmet mandatory field via `errors` (see the
  // per-field `{errors.x?.message}` blocks below) rather than silently doing
  // nothing. A model-discovery failure/in-flight check isn't caught by the
  // schema (it's separate component state), so it's guarded explicitly here
  // — otherwise a stale-but-non-empty model value from before the endpoint
  // broke would let an invalid configuration through.
  const submit = handleSubmit(async (values) => {
    if (modelsError || modelsLoading) return;
    await onSubmit(values);
  });

  return (
    <form
      id="id-integration-form"
      data-testid="integration-form"
      className="space-y-6"
      onSubmit={submit}
    >
      {/* Name */}
      <div className="space-y-2">
        <Label htmlFor="id-name">
          {t('name')} <span className="text-red-500">*</span>
        </Label>
        <Controller
          name="name"
          control={control}
          render={({ field }) => (
            <Input
              id="id-name"
              className={inputClasses}
              placeholder={t('enter-name')}
              readOnly={readOnly || mode === 'edit'}
              {...field}
            />
          )}
        />
        {mode === 'edit' && (
          <p className="text-xs italic text-amber-600">
            ⓘ Integration name cannot be edited after creation
          </p>
        )}
        {(errors as any).name?.message && (
          <p className="text-xs text-red-500">
            {t((errors as any).name.message)}
          </p>
        )}
        {!(errors as any).name?.message && nameWarning && (
          <p className="text-xs text-red-500">{nameWarning}</p>
        )}
      </div>

      {/* Provider */}
      <div className="space-y-2">
        <Label htmlFor="id-provider">{t('provider')}</Label>
        <Select
          value={provider}
          onValueChange={(val) => handleProviderChange(val as Provider)}
          disabled={mode !== 'create'}
        >
          <SelectTrigger id="id-provider">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {getAvailableProviders(services).map((p) => (
              <SelectItem key={p} value={p} textValue={t(p)}>
                <span className="flex items-center gap-2">
                  <ProviderIcon provider={p} className="size-4 shrink-0" />
                  <span>{t(p)}</span>
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {(errors as any).provider?.message && (
          <p className="text-xs text-red-500">
            {t((errors as any).provider.message)}
          </p>
        )}
      </div>

      {/* apiKey — every provider except ollama */}
      {provider !== 'ollama' && (
        <div className="space-y-2">
          <Label htmlFor="id-apiKey">
            {t('api-key')} <span className="text-red-500">*</span>
          </Label>
          <div className="relative">
            <Controller
              name={'apiKey' as any}
              control={control}
              render={({ field }) => (
                <Input
                  id="id-apiKey"
                  type={showApiKey ? 'text' : 'password'}
                  className={`${inputClasses} pr-10`}
                  placeholder={t('enter-api-key')}
                  readOnly={readOnly}
                  {...field}
                />
              )}
            />
            <button
              type="button"
              onClick={() => setShowApiKey((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
            >
              <span className="sr-only">{t('show-api-key')}</span>
              {showApiKey ? <ViewOff size={16} /> : <View size={16} />}
            </button>
          </div>
          {(errors as any).apiKey && (
            <p className="text-xs text-red-500">{t('api-key-required')}</p>
          )}
        </div>
      )}

      {/* apiEndpoint — required for watsonx/azure/ollama, optional for openai/claude/gemini */}
      {(provider === 'ibm-watson-x' ||
        provider === 'azure-openai' ||
        provider === 'azure-openai-gateway' ||
        provider === 'ollama' ||
        provider === 'openai' ||
        provider === 'claude' ||
        provider === 'gemini') && (
        <div className="space-y-2">
          <Label htmlFor="id-apiEndpoint">
            {t('api-endpoint')}{' '}
            {(provider === 'ibm-watson-x' ||
              provider === 'azure-openai' ||
              provider === 'azure-openai-gateway' ||
              provider === 'ollama') && <span className="text-red-500">*</span>}
          </Label>
          <Controller
            name={'apiEndpoint' as any}
            control={control}
            render={({ field }) => (
              <Input
                id="id-apiEndpoint"
                className={inputClasses}
                placeholder={t('enter-api-endpoint')}
                readOnly={readOnly}
                {...field}
                value={field.value ?? ''}
              />
            )}
          />
          {(provider === 'openai' ||
            provider === 'claude' ||
            provider === 'gemini') &&
            !(errors as any).apiEndpoint?.message && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t('api-endpoint-optional-hint')}
              </p>
            )}
          {(errors as any).apiEndpoint?.message && (
            <p className="text-xs text-red-500">
              {t((errors as any).apiEndpoint.message)}
            </p>
          )}
        </div>
      )}

      {/* watsonx-only: projectId */}
      {provider === 'ibm-watson-x' && (
        <div className="space-y-2">
          <Label htmlFor="id-projectId">
            {t('project-id')} <span className="text-red-500">*</span>
          </Label>
          <Controller
            name={'projectId' as any}
            control={control}
            render={({ field }) => (
              <Input
                id="id-projectId"
                className={inputClasses}
                placeholder={t('enter-project-id')}
                readOnly={readOnly}
                {...field}
              />
            )}
          />
          {(errors as any).projectId?.message && (
            <p className="text-xs text-red-500">
              {t((errors as any).projectId.message)}
            </p>
          )}
        </div>
      )}

      {/* azure-only: deployment name + api version (+ optional embedding deployment) */}
      {(provider === 'azure-openai' || provider === 'azure-openai-gateway') && (
        <>
          <div className="space-y-2">
            <Label htmlFor="id-azureOpenAIApiDeploymentName">
              {t('azure-openai-deployment-name')}{' '}
              <span className="text-red-500">*</span>
            </Label>
            <Controller
              name={'azureOpenAIApiDeploymentName' as any}
              control={control}
              render={({ field }) => (
                <Input
                  id="id-azureOpenAIApiDeploymentName"
                  className={inputClasses}
                  placeholder={t('enter-azure-openai-deployment-name')}
                  readOnly={readOnly}
                  {...field}
                />
              )}
            />
            {(errors as any).azureOpenAIApiDeploymentName?.message && (
              <p className="text-xs text-red-500">
                {t((errors as any).azureOpenAIApiDeploymentName.message)}
              </p>
            )}
          </div>
          {services?.azureEmbeddingDeployment?.show && (
            <div className="space-y-2">
              <Label htmlFor="id-azureOpenAIEmbeddingDeploymentName">
                {t('azure-openai-embedding-deployment-name')}
              </Label>
              <Controller
                name={'azureOpenAIEmbeddingDeploymentName' as any}
                control={control}
                render={({ field }) => (
                  <Input
                    id="id-azureOpenAIEmbeddingDeploymentName"
                    className={inputClasses}
                    placeholder={t(
                      'enter-azure-openai-embedding-deployment-name',
                    )}
                    readOnly={readOnly}
                    {...field}
                  />
                )}
              />
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t('azure-openai-embedding-deployment-hint')}
              </p>
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="id-azureOpenAIApiVersion">
              {t('azure-openai-api-version')}{' '}
              <span className="text-red-500">*</span>
            </Label>
            <Controller
              name={'azureOpenAIApiVersion' as any}
              control={control}
              render={({ field }) => (
                <Input
                  id="id-azureOpenAIApiVersion"
                  className={inputClasses}
                  placeholder={t('enter-azure-openai-api-version')}
                  readOnly={readOnly}
                  {...field}
                />
              )}
            />
            {(errors as any).azureOpenAIApiVersion?.message && (
              <p className="text-xs text-red-500">
                {t((errors as any).azureOpenAIApiVersion.message)}
              </p>
            )}
          </div>
        </>
      )}

      {/* model — every provider except azure (deployment name IS the model there) */}
      {provider !== 'azure-openai' && provider !== 'azure-openai-gateway' && (
        <div className="space-y-2">
          <Label htmlFor="id-model" className="flex items-center gap-2">
            {t('model')} <span className="text-red-500">*</span>
            {modelsLoading && (
              <span
                data-testid="model-loading-spinner"
                role="status"
                aria-label={t('loading-models')}
                className="inline-block size-4 animate-spin rounded-full border-2 border-brand-interactive border-t-transparent dark:border-brand-blue dark:border-t-transparent"
              />
            )}
          </Label>
          <Controller
            name={'model' as any}
            control={control}
            render={({ field }) => (
              <Autocomplete
                freeSolo
                clearOnEscape
                disabled={readOnly || modelsLoading || !!modelsError}
                options={modelOptions}
                value={field.value || ''}
                onChange={(_, newValue) => field.onChange(newValue ?? '')}
                onInputChange={(_, newInputValue) =>
                  field.onChange(newInputValue || '')
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    inputProps={{ ...params.inputProps, id: 'id-model' }}
                    placeholder={t('enter-model-name')}
                    size="small"
                  />
                )}
              />
            )}
          />
          {modelsError && <p className="text-sm text-red-500">{modelsError}</p>}
          {(errors as any).model?.message && (
            <p className="text-xs text-red-500">
              {t((errors as any).model.message)}
            </p>
          )}
        </div>
      )}

      {/* Ollama — per-model output ceiling, always shown; reasoning is
          Automatic (reasoningEnabled false/unset — nothing sent, the model's
          own default behavior) by default, with Custom revealing the
          freeform override. Ollama is not in REASONING_MODEL_PROVIDERS and
          showsReasoning never covers it, so this is its own block. */}
      {provider === 'ollama' && (
        <>
          <div className="space-y-2">
            <Label htmlFor="id-output-ceiling">{t('output-ceiling')}</Label>
            <Controller
              name={'outputCeiling' as any}
              control={control}
              render={({ field }) => (
                <Input
                  id="id-output-ceiling"
                  data-testid="ollama-output-ceiling-input"
                  type="number"
                  className={inputClasses}
                  placeholder={t('output-ceiling-placeholder')}
                  readOnly={readOnly}
                  {...field}
                  value={field.value ?? ''}
                />
              )}
            />
            {(errors as any).outputCeiling?.message ? (
              <p className="text-xs text-red-500">
                {t((errors as any).outputCeiling.message)}
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">
                {t('output-ceiling-description')}
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="id-ollama-reasoning-mode">{t('reasoning')}</Label>
            <p className="text-xs text-muted-foreground">
              {t('reasoning-mode-description')}
            </p>
            <Controller
              name={'reasoningEnabled' as any}
              control={control}
              render={({ field }) => (
                <Select
                  value={field.value ? 'custom' : 'automatic'}
                  onValueChange={(v) => field.onChange(v === 'custom')}
                  disabled={readOnly}
                >
                  <SelectTrigger
                    id="id-ollama-reasoning-mode"
                    data-testid="ollama-reasoning-mode-select"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="automatic">{t('automatic')}</SelectItem>
                    <SelectItem value="custom">{t('custom')}</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
          </div>
          {/* Bug fix: this used to always render (readOnly, not hidden) even
              with reasoning disabled, so the field sat there uselessly under
              an unchecked checkbox. Now it doesn't render at all outside
              Custom mode. */}
          {ollamaReasoningEnabled && (
            <div className="space-y-2">
              <Label htmlFor="id-reasoning">{t('reasoning-value')}</Label>
              <Controller
                name={'reasoning' as any}
                control={control}
                render={({ field }) => (
                  <Input
                    id="id-reasoning"
                    data-testid="ollama-reasoning-input"
                    className={inputClasses}
                    placeholder={t('reasoning-placeholder')}
                    readOnly={readOnly}
                    {...field}
                    value={field.value ?? ''}
                  />
                )}
              />
              {(errors as any).reasoning?.message ? (
                <p className="text-xs text-red-500">
                  {t((errors as any).reasoning.message)}
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  {t('reasoning-value-hint')}
                </p>
              )}
            </div>
          )}
        </>
      )}

      {/* Advanced — reasoning, only for reasoning-capable models on openai/claude/gemini */}
      {showsReasoning && (
        <Collapsible defaultOpen>
          <CollapsibleTrigger className="flex w-full items-center justify-between gap-2 text-sm font-semibold [&[data-state=open]>svg]:rotate-180">
            {t('advanced-options')}
            <ChevronDown className="size-4 shrink-0 transition-transform duration-300" />
          </CollapsibleTrigger>
          <CollapsibleContent className="space-y-4 pt-4">
            <div className="flex items-center gap-2">
              <Controller
                name={'reasoningEnabled' as any}
                control={control}
                render={({ field }) => (
                  <input
                    type="checkbox"
                    id="id-reasoning-enabled"
                    checked={!!field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                    disabled={readOnly}
                    className="size-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                )}
              />
              <Label htmlFor="id-reasoning-enabled">
                {t('enable-reasoning')}
              </Label>
            </div>

            {provider === 'claude' && (
              <div className="space-y-2">
                <Label htmlFor="id-thinking-budget">{t('budget-tokens')}</Label>
                <Controller
                  name={'thinkingBudget' as any}
                  control={control}
                  render={({ field }) => (
                    <Input
                      type="number"
                      id="id-thinking-budget"
                      className={inputClasses}
                      min={1000}
                      max={100000}
                      value={field.value ?? 10000}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                      disabled={readOnly}
                    />
                  )}
                />
              </div>
            )}

            {provider === 'openai' && (
              <div className="space-y-2">
                <Label htmlFor="id-reasoning-effort">
                  {t('reasoning-effort')}
                </Label>
                <Controller
                  name={'reasoningEffort' as any}
                  control={control}
                  render={({ field }) => (
                    <Select
                      value={field.value || 'medium'}
                      onValueChange={field.onChange}
                      disabled={readOnly}
                    >
                      <SelectTrigger id="id-reasoning-effort">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">{t('low')}</SelectItem>
                        <SelectItem value="medium">{t('medium')}</SelectItem>
                        <SelectItem value="high">{t('high')}</SelectItem>
                        <SelectItem value="xhigh">{t('extra-high')}</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
            )}

            {provider === 'gemini' && (
              <div className="space-y-2">
                <Label htmlFor="id-thinking-level">{t('thinking-level')}</Label>
                <Controller
                  name={'thinkingLevel' as any}
                  control={control}
                  render={({ field }) => (
                    <Select
                      value={field.value || 'medium'}
                      onValueChange={field.onChange}
                      disabled={readOnly}
                    >
                      <SelectTrigger id="id-thinking-level">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">{t('low')}</SelectItem>
                        <SelectItem value="medium">{t('medium')}</SelectItem>
                        <SelectItem value="high">{t('high')}</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
            )}
          </CollapsibleContent>
        </Collapsible>
      )}

      {!readOnly && (
        <div className="flex justify-end gap-3 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isSaving}
          >
            {t('cancel')}
          </Button>
          <Button type="submit" disabled={isSaving || !!nameWarning}>
            {isSaving ? t('saving') : t('save')}
          </Button>
        </div>
      )}
    </form>
  );
};

export default IntegrationForm;
